import React, { memo, useMemo, useCallback, useState, useEffect } from 'react';
import { Proposal } from '../types';
import { BlockContainer } from '../BlockContainer';
import { useHistory } from '@core/router';
import './index.scss';
import classNames from 'classnames';
import {
  formGroup,
  formControl,
  FormDataType,
  formArray,
  useFormConfig,
  useFormState,
  Validators,
  useFormControlState,
  ValidationStatus,
} from '@core/utils/form';
import { defineBlockAdvanceForm } from '../Common/AdvanceSettings';
import { useNonNilObservable, useObservable } from '@core/utils/hooks/rxjs';
import { useConfigureBlock } from '@modules/proposal/hooks';
import { UploadAPI } from '@business/api/upload';
import { tap } from 'rxjs/operators';
import { Container } from 'reactstrap';
import { FormText } from '@core/components/form/FormText';
import { useDidMount } from '@core/utils/hooks/react';
import { useProposalContext, useProposalDataContext } from '@modules/proposal/ProposalBuilderPage';
import { FormInput, FormCheckbox } from '@core/components/form';
import { ProposalAPI } from '@business/api/proposal_api';
import { defineProposalForm } from '@modules/proposal/ProposalBuilderPage';
import { Button } from '@core/components/Button';
import { ProposalService, HTMLService } from '@business/services';
import { ProposalPreviewPageBloc } from '@modules/proposals/ViewProposalModal/ProposalPreviewPage/ProposalPreviewPageBloc';
import { SignerBlockSettings } from './SignerBlockSettings';
import { R } from '@core/utils/r';
import { DOMNode } from 'html-react-parser';
import moment from 'moment';
import { of } from 'rxjs';
import { ProposalStatus } from '@business/entities/proposal';
import { useSettings } from '@core/utils/hooks/misc/useSettings';

function defineSignerBlockItemForm(terms: number) {
  return formGroup({
    sign: formControl<string>({
      placeholder: 'Type here to create signature...',
      required: true,
    }),
    email: formControl<string>({}),
    canSign: formControl<boolean>({}),
    name: formControl<string>({
      initialValue: 'John Doe',
    }),
    menuOrder: formControl<number>({
      initialValue: 0,
    }),
    terms: formGroup({
      ...R.fromPairs(R.times(terms).map(i => [`term-${i}`, formControl<boolean>({ initialValue: false, required: true })])),
    }),
  });
}

function defineSignerBlockForm(terms: number) {
  return formGroup({
    items: formArray(() => defineSignerBlockItemForm(terms)),
  });
}
export type SignerWidgetItemFormData = FormDataType<typeof defineSignerBlockItemForm>;
export type SignerWidgetFormData = FormDataType<typeof defineSignerBlockForm>;
export type SignerWidgetForm = ReturnType<typeof defineSignerBlockForm>;

export enum SignerBlockVariation {
  Signer = 'Signer',
}

export const SignerBlock = memo((props: Proposal.BlockProps<SignerBlockVariation>) => {
  const { variation, preview, editable } = props;
  const [init, setInit] = useState(false);
  const [currentSignee, setCurrentSignee] = useState(0);
  const proposal = useProposalContext();
  const history = useHistory();
  const proposalData = useProposalDataContext();
  const [isAccepted, setIsAccepted] = useState(true);
  const senders = useMemo(() => proposalData?.senders || [], [proposalData]);
  const signee = useMemo(() => {
    if (proposalData?.signee) {
      return proposalData.signee;
    } else {
      return null;
    }
  }, [proposalData]);

  const mainProposalForm = useMemo(() => {
    if (proposal) return proposal.form;
  }, [proposal]);

  const settings = useSettings();
  const proposalSettings = useMemo(() => settings?.proposalSettings, [settings]);
  const submitProposalButtonText = useMemo(() => (proposalSettings ? proposalSettings.submitProposalButtonText : 'Submit'), [proposalSettings]);
  const proposalSubmitButtonText = useMemo(
    () => (R.isEmpty(submitProposalButtonText) || R.isNull(submitProposalButtonText) ? 'Submit' : submitProposalButtonText),
    [submitProposalButtonText],
  );
  const mainFormValues = useObservable(mainProposalForm && mainProposalForm.value$);
  const termsValue = useObservable(proposal?.form.controls.terms.value$, '');
  const productValues = useObservable(proposal?.form.controls.products.value$, []);

  const termsOccurance = useMemo(() => R.occurrences(termsValue || '', '[]'), [termsValue]);

  const form = useMemo(() => defineSignerBlockForm(termsOccurance), [termsOccurance]);
  const itemValues = useObservable(form.controls.items.items$, []);

  useEffect(() => {
    if (signee && itemValues.length > 0 && !init) {
      setInit(true);
      itemValues.map((ctrl, index) => {
        const isRequired = ctrl.value.email == signee.email;
        ctrl.controls.sign.setConfig({ required: isRequired });
        setCurrentSignee(index);
        R.times(termsOccurance).map(i => {
          ctrl.controls.terms.controls[`term-${i}`].setConfig({ required: isRequired });
        });
      });
    }
  }, [init, itemValues, itemValues.length, signee, termsOccurance]);

  const formValue = useNonNilObservable(form.value$);
  const [signature, setSignature] = useState(true);
  const previewPageBloc = proposal && (proposal.bloc as ProposalPreviewPageBloc);

  const onSubmit = useCallback(() => {
    if (previewPageBloc && signee) {
      const selectedProducts = productValues.filter(p => !!p.selected);
      const item = R.find(itemValues, i => i.value.email == signee.email);
      if (mainFormValues && item) {
        return previewPageBloc
          .updateSender(mainFormValues, signee.id, { isSigned: 1, signedName: item.value.sign, email: item.value.email }, selectedProducts)
          .pipe(
            tap(() => setSignature(false)),
            tap(() => setCurrentSignee(0)),
            tap(data => {
              const senders = data.senders;
              if (senders && senders.every(s => s.is_signed) && !R.isEmpty(data.depositUrl)) {
                window.open(data.depositUrl, '_self');
              }
              //   return data.depositUrl && !R.isEmpty(data.depositUrl) && window.open(data.depositUrl, '_self');
            }),
          );
      }
    }
    return of(null);
  }, [itemValues, mainFormValues, previewPageBloc, productValues, signee]);

  useDidMount(() => {
    form.controls.items.push().patchValue({ menuOrder: 0 });
  });

  useConfigureBlock({ props, form, contentComponent: SignerBlockSettings, contentProps: { form, terms: proposal?.form.controls.terms } });
  const termsHtml = useMemo(() => {
    if (proposal) {
      const terms = proposal.form.value.terms;
      if (terms) {
        const arrayItems = terms.split('[]');
        let newString = '';
        arrayItems.forEach((item, index) => {
          newString =
            newString.concat(item) + (arrayItems.length === index + 1 ? '' : `<span class="proposal-checkbox" data-index="${index}">[]</span>`);
        });
        return newString;
      }
    }
    return '<span></span>';
  }, [proposal]);

  const htmlOptions = useCallback(
    (domNode: DOMNode, email = null, currentSigneeIndex = 0, isDisabled = false) => {
      // If dom node it a then open it in new tab
      if (R.get(domNode, 'name') === 'a') {
        return (
          <a href={R.get(domNode, 'attribs.href')} target="_blank" rel="noopener noreferrer">
            {R.get(domNode, 'children[0].data')}
          </a>
        );
      }
      if (R.get(domNode, 'attribs.class', '').includes('proposal-checkbox')) {
        const index = R.get(domNode, 'attribs.data-index');
        if (
          R.get(form.controls.items.items, currentSigneeIndex) &&
          R.get(form.controls.items.items[currentSigneeIndex].controls.terms.controls, `term-${index}`)
        ) {
          const isChecked = !R.isEmpty(senders.find(s => s.email === email)?.signed_at);
          if (isChecked) {
            form.controls.items.items[currentSigneeIndex].controls.terms.controls[`term-${index}`].setValue(true);
            form.controls.items.items[currentSigneeIndex].controls.terms.controls[`term-${index}`].setConfig({ disabled: true });
          }
          if (isDisabled) {
            const value = form.controls.items.items[currentSigneeIndex].controls.terms.controls[`term-${index}`].value;
            return (
              <FormCheckbox
                noPadding
                control={form.controls.items.items[currentSigneeIndex].controls.terms.controls[`term-${index}`]}
                className="d-inline"
                disabled={true}
              />
            );
          }
          return (
            <FormCheckbox
              noPadding
              control={form.controls.items.items[currentSigneeIndex].controls.terms.controls[`term-${index}`]}
              className="d-inline"
              // disabled={props.preview}
            />
          );
        }
        return domNode;
      }
      return domNode;
    },
    [form.controls.items.items, senders],
  );

  useFormConfig(form, { onSubmit });
  const { submitting, status } = useFormState(form, { submitting: true, status: true });

  useEffect(() => {
    if (status == ValidationStatus.valid) {
      setIsAccepted(true);
    }
  }, [status]);

  const onPreSubmit = useCallback(() => {
    setIsAccepted(status === ValidationStatus.valid);
  }, [status]);

  const pageCanSign = useMemo(() => {
    return history.location.pathname == '/proposal-preview';
  }, [history.location.pathname]);

  return (
    <BlockContainer className={classNames('block-signer', variation, preview)}>
      <Container>
        <div style={{ display: 'flex' }}>
          {props.editable && (
            <div className="signer-centered" style={{ flex: 1 }}>
              <FormInput control={formControl({ placeholder: 'John Smith' })} editable={false} disabled={true} />
              {HTMLService.toReact(
                !props.preview
                  ? termsHtml
                  : '<span>By signing I agree this is a legal representation of my signature for all purposes just the same as pen-and-paper signature.</span>',
                htmlOptions,
              )}
            </div>
          )}

          {!props.editable &&
            itemValues.map((ctrl, i) => {
              const canSign = signee && signee.email == ctrl.controls.email.value && signee.can_sign && !signee.is_signed && signature ? true : false;
              const showSignatureArea = !R.isEmpty(ctrl.controls.sign.value) || (canSign && signature);
              const sender = senders.find(s => s.email === ctrl.controls.email.value);
              const showTerms = canSign && signature && pageCanSign;
              const isCurrentSignee = senders.find(s => s.email == ctrl.controls.email.value);
              return (
                <div className="signer-centered mr-2" key={i} style={{ flex: 1 }}>
                  <FormText control={ctrl.controls.name} editable={editable} />

                  <div className="mt-2">
                    <FormInput control={ctrl.controls.sign} editable={editable} disabled={!canSign} />
                    {showTerms && (
                      <>
                        {HTMLService.toReact(termsHtml, e => htmlOptions(e, ctrl.controls.email.value, i))}
                        {!isAccepted && termsOccurance > 0 && <div className="text-danger ">Please enter your name and accept all terms</div>}
                        <Button
                          loading={submitting}
                          extraClassNames="mt-2"
                          onClick={() => {
                            form.submit(() => {
                              setCurrentSignee(i);
                              onPreSubmit();
                            });
                          }}
                          intent="primary"
                        >
                          {proposalSubmitButtonText || 'Submit'}
                        </Button>
                      </>
                    )}

                    {!pageCanSign && HTMLService.toReact(termsHtml, e => htmlOptions(e, ctrl.controls.email.value, i, true))}

                    {/* {!showTerms &&
                      HTMLService.toReact(
                        !props.preview
                          ? termsHtml
                          : '<span>By signing I agree this is a legal representation of my signature for all purposes just the same as pen-and-paper signature.</span>',
                        e => htmlOptions(e, ctrl.controls.email.value),
                      )} */}

                    {!!sender && sender.is_signed == 1 && (
                      <>
                        {/* {!pageCanSign && HTMLService.toReact(termsHtml, e => htmlOptions(e, ctrl.controls.email.value))} */}
                        <span className="text-muted font-size-12">Signed on {moment(sender.signed_at).format('MM/DD/YYYY [at] YYYY hh:mm A')}</span>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
        </div>
      </Container>
    </BlockContainer>
  );
});
